import { Feature } from 'ol';
import { getCenter } from 'ol/extent';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { Vector as VectorLayer } from 'ol/layer';
import RenderFeature from 'ol/render/Feature';
import { Cluster, Vector as VectorSource } from 'ol/source';
import { Fill, Stroke, Style, Text, Icon, Circle } from 'ol/style';
import { StyleFunction } from 'ol/style/Style';
import {
  CLUSTER_DISTANCE,
  CLUSTER_FONT_FAMILY,
  CLUSTER_LAYER,
  EVENT_DATA,
  GEOFENCE_LAYER,
  IS_INVISIBLE,
  POINT,
  TRACKING_BOUNDS,
} from '../../../util/constants';
import { transform } from 'ol/proj';
import AnimatedCluster from 'ol-ext/layer/AnimatedCluster';
import { Draw, Modify } from 'ol/interaction';
import { PRIMARY, WHITE } from '../../../Style/GeoMobyBaseTheme';
import { getExtentDifference } from '../../../hooks/geomoby/MapAnimation';
import { StrongFeatureHolder } from '../../../hooks/geomoby/useLiveMapLoader';
import { ZIndexes } from '../../../util/ZIndexes';
import {
  FenceEntityType,
  FenceZone,
  Interactions,
  EBTTriggerTypeOptionsValue,
} from '../../../util/enums';
import { geometryTypeOfEntity, microfenceTypeOfEntity } from '../commons';
import { getMicrofenceStyleFromType } from './MicrofenceStyles';
import {
  geofenceStyleDefaults,
  dwellIcon,
  entryBlueIcon,
  exitRedIcon,
  measurementTool,
  leftToRightIcon,
  rightToLeftIcon,
  appEntryBlueIcon,
  appExitRedIcon,
  appLeftToRightBlueIcon,
  appRightToLeftRedIcon,
} from './MiscStyles';
import { LayerParts } from '../types';
import { SimpleGeofence } from '../../../util/Events/Messages';
import { GeofenceEntityType } from '../../../util/enums';

export const textStroke = new Stroke({
  color: 'rgba(0, 0, 0, 0.6)',
  width: geofenceStyleDefaults.default.width,
});

/* Default Zone */
export const defaultFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.default.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.default.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const selectedDefaultFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.default.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.default.stroke,
    width: geofenceStyleDefaults.selected.width,
    lineDash: geofenceStyleDefaults.selected.lindash,
  }),
  zIndex: ZIndexes.GEOFENCE,
});

/* Breach Zone */
export const breachFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.breach.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.breach.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE_FOREGROUND,
});
export const selectedBreachFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.breach.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.breach.stroke,
    width: geofenceStyleDefaults.selected.width,
    lineDash: geofenceStyleDefaults.selected.lindash,
  }),
  zIndex: ZIndexes.GEOFENCE_FOREGROUND,
});

/* Buffer Zone */
export const bufferFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.buffer.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.buffer.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const selectedBufferFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.buffer.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.buffer.stroke,
    width: geofenceStyleDefaults.selected.width,
    lineDash: geofenceStyleDefaults.selected.lindash,
  }),
  zIndex: ZIndexes.GEOFENCE,
});

/* Cleared Zone */
export const clearedFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.cleared.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.cleared.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const selectedClearedFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.cleared.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.cleared.stroke,
    width: geofenceStyleDefaults.selected.width,
    lineDash: geofenceStyleDefaults.selected.lindash,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const clearedFenceDrawStartStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.cleared.fill }),
  stroke: new Stroke({ color: [0, 0, 0, 0], width: geofenceStyleDefaults.default.width }),
});

/* Tracking Bounds */
export const trackingFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.tracking.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.tracking.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const selectedTrackingFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.tracking.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.tracking.stroke,
    width: geofenceStyleDefaults.selected.width,
    lineDash: geofenceStyleDefaults.selected.lindash,
  }),
  zIndex: ZIndexes.GEOFENCE,
});
export const trackingFenceDrawStartStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.tracking.fill }),
  stroke: new Stroke({ color: [0, 0, 0, 0], width: geofenceStyleDefaults.default.width }),
});

/* Invisible */
export const invisibleFenceStyle = new Style({
  fill: new Fill({ color: geofenceStyleDefaults.invisible.fill }),
  stroke: new Stroke({
    color: geofenceStyleDefaults.invisible.stroke,
    width: geofenceStyleDefaults.default.width,
  }),
  zIndex: ZIndexes.GEOFENCE_FOREGROUND,
});

/* Clusters */
const innerCluster = new Circle({
  radius: 10,
  fill: new Fill({
    color: 'rgba(0, 184, 217, 0.7)',
  }),
});
const outerCluster = new Circle({
  radius: 18,
  fill: new Fill({
    color: 'rgba(0, 184, 217, 0.3)',
  }),
});
const innerTrackingBoundCluster = new Circle({
  radius: 10,
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.7)',
  }),
});
const outerTrackingBoundCluster = new Circle({
  radius: 18,
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.3)',
  }),
});

const iconByEvent = {
  app: {
    Entry: appEntryBlueIcon,
    Exit: appExitRedIcon,
    LeftToRight: appLeftToRightBlueIcon,
    RightToLeft: appRightToLeftRedIcon,
    Dwell: dwellIcon,
  },
  backend: {
    Entry: entryBlueIcon,
    Exit: exitRedIcon,
    LeftToRight: leftToRightIcon,
    RightToLeft: rightToLeftIcon,
    Dwell: dwellIcon,
  },
};

export const initialiseGeofenceClustersOverlay = (pointLayer: {
  id: string;
  name: string;
  source: StrongFeatureHolder<Point, SimpleGeofence>[];
}): AnimatedCluster => {
  const id = `${POINT}_${pointLayer.id}`;
  const vs = new VectorSource();

  vs.addFeatures(
    pointLayer.source.map(fence => {
      fence.feature.set('id', fence.data.id);
      fence.feature.set('name', fence.data.name);
      fence.feature.set('layerId', fence.data.layerId);
      fence.feature.set('type', fence.data.type);
      fence.feature.set('fenceType', fence.data.fenceType);
      return fence.feature;
    }),
  );

  const layer = new AnimatedCluster({
    className: CLUSTER_LAYER,
    source: new Cluster({
      source: vs,
      distance: CLUSTER_DISTANCE,
    }),
    style: clusterStyle(pointLayer.name),
    properties: { id, name: pointLayer.name },
  });
  return layer;
};
export const clusterStyle = (layerName: string): StyleFunction => {
  return (cluster: Feature<Geometry> | RenderFeature) => {
    const size = cluster.get('features').length;
    let extentDifference = Number.MAX_SAFE_INTEGER;
    let styles: Style[] = [];
    if (size > 1) {
      const features: Feature<Geometry>[] = cluster.get('features');
      const firstExent = cluster.get('features')[0].getGeometry()?.getExtent();
      const center = getCenter(firstExent);
      styles.push(geofenceMarkerStyle({ geometry: new Point([center[0], center[1]]) }));

      for (let f = 1; f < features.length; f++) {
        const currentExtent = cluster.get('features')[f].getGeometry()?.getExtent();
        if (firstExent && currentExtent) {
          extentDifference = getExtentDifference(firstExent, currentExtent);
          if (extentDifference <= 1) {
            const center = getCenter(currentExtent);
            styles.push(
              geofenceMarkerStyle({ geometry: new Point([center[0] + f * 20, center[1]]) }),
            );
          } else {
            styles = [];
            break;
          }
        }
      }
    }

    if (styles.length > 0 && extentDifference <= 1) {
      return styles;
    }

    if (size <= 1) {
      return geofenceMarkerStyle({ fenceType: cluster.get('features')[0].get('fenceType') });
    }

    return [
      new Style({
        image: layerName === TRACKING_BOUNDS ? outerTrackingBoundCluster : outerCluster,
        zIndex: ZIndexes.MARKER,
      }),
      new Style({
        image: layerName === TRACKING_BOUNDS ? innerTrackingBoundCluster : innerCluster,
        zIndex: ZIndexes.MARKER,
        text: new Text({
          text: size.toString(),
          font: CLUSTER_FONT_FAMILY,
          fill: new Fill({
            color: layerName === TRACKING_BOUNDS ? PRIMARY : WHITE,
          }),
          stroke: textStroke,
        }),
      }),
    ];
  };
};

/* Layers */
export const updateLayerOverlay = (
  layers: { id: string; name: string; source: StrongFeatureHolder<Polygon, SimpleGeofence>[] }[],
): { id: string; name: string; source: VectorLayer<VectorSource<Geometry>> }[] | undefined => {
  const styleCache = new Map<string, Style[]>();
  return layers.map(layer => {
    const source = new VectorSource();
    source.setProperties({ id: layer.id, name: layer.name });

    const vectorLayer = new VectorLayer({
      source,
      className: GEOFENCE_LAYER,
      style: defaultGeofencesStyle(styleCache),
    });

    layer.source.forEach(f => source.addFeature(f.feature));
    return { id: layer.id, name: layer.name ?? layer.id, source: vectorLayer };
  });
};

/* Geofences */
export const defaultGeofencesStyle = (cache: Map<string, Style | Style[]>): StyleFunction => {
  return (geofence: Feature<Geometry> | RenderFeature) => {
    const isSelected = geofence.get('selected');
    const isInvisible = geofence.get(IS_INVISIBLE);
    const cachePrefix = geofence.get('id')
      ? `${isSelected ? 'selected_' : ''}${geofence.get('id')}`
      : '';
    const zoneSuffix = geofence.get('zone');
    const key = `${cachePrefix}${zoneSuffix ? '-' + zoneSuffix : ''}`;

    if (isInvisible) cache.clear();
    if (cachePrefix && cache.has(key)) {
      return cache.get(key);
    }
    const style = geofence.get('id')
      ? geofencesStyle(geofence)
      : isSelected
      ? selectedDefaultFenceStyle
      : defaultFenceStyle;
    if (style) {
      cache.set(key, style);
    }
    return style;
  };
};

export const getGeofenceStyleFromZone = (props: {
  zone: FenceZone;
  type?: FenceEntityType;
  layerName?: string;
  selected?: boolean;
  isLocationTrace?: boolean;
  isLocationTraceDotted?: boolean;
  isInvisible?: boolean;
}): Style => {
  if (props.isInvisible) {
    return invisibleFenceStyle;
  }
  if (props.zone === FenceZone.breach || props.isLocationTrace) {
    return props.selected ? selectedBreachFenceStyle : breachFenceStyle;
  } else if (props.isLocationTraceDotted) {
    return selectedBreachFenceStyle;
  } else if (props.zone === FenceZone.buffer) {
    return props.selected ? selectedBufferFenceStyle : bufferFenceStyle;
  } else if (props.zone === FenceZone.cleared || props.type === FenceEntityType.Multipolygon) {
    return props.selected ? selectedClearedFenceStyle : clearedFenceStyle;
  } else if (props.layerName === TRACKING_BOUNDS && props.type !== FenceEntityType.Line) {
    return props.selected ? selectedTrackingFenceStyle : trackingFenceStyle;
  } else {
    return props.selected ? selectedDefaultFenceStyle : defaultFenceStyle;
  }
};
const geofencesStyle = (feature: RenderFeature | Feature<Geometry>): Style | Style[] => {
  const {
    selected,
    zone,
    layerName,
    geometry,
    isMeasurementTool,
    IS_INVISIBLE,
    IS_LOCATION_TRACE,
    IS_LOCATION_TRACE_DOTTED,
  } = feature.getProperties();
  const type = geometryTypeOfEntity(feature.getProperties());
  if (type === FenceEntityType.Microfence) {
    const microfenceType = microfenceTypeOfEntity(feature.getProperties());
    if (feature.getGeometry() instanceof Point && microfenceType) {
      return getMicrofenceStyleFromType(microfenceType, selected);
    }
  }
  const style = getGeofenceStyleFromZone({
    zone,
    type,
    layerName,
    selected,
    isInvisible: IS_INVISIBLE,
    isLocationTrace: IS_LOCATION_TRACE,
    isLocationTraceDotted: IS_LOCATION_TRACE_DOTTED,
  });
  if (type !== FenceEntityType.Line) {
    return style;
  }
  return isMeasurementTool
    ? measurementTool
    : [style, ...tripwiresStyle(geometry.flatCoordinates, IS_INVISIBLE)];
};
export const updateGeofencesStyle = (
  cache: Map<string, Style | Style[]>,
  fenceId?: string | null,
): StyleFunction => {
  const cachePrefix = fenceId ? `selected_${fenceId}` : '';
  return (geofence: Feature<Geometry> | RenderFeature) => {
    const zoneSuffix = geofence.get('zone');
    const key = `${cachePrefix}${zoneSuffix ? '-' + zoneSuffix : ''}`;

    if (fenceId) cache.clear();
    if (cachePrefix && cache.has(key)) {
      return cache.get(key);
    }
    const style = geofencesStyle(geofence);
    if (style) {
      cache.set(key, style);
    }
    return style;
  };
};

/* Tripwires */
const tripwireArrowStyle = (point: Point, rotation: number): Style => {
  return new Style({
    geometry: point,
    image: new Icon({
      src: '../../../triangle.svg',
      rotateWithView: true,
      rotation: rotation,
      scale: 1,
    }),
  });
};
export const tripwiresStyle = (coordinates: number[], isInvisible?: boolean): Style[] => {
  if (isInvisible) return [];
  const coordsA: number[] = transform([coordinates[0], coordinates[1]], 'EPSG:3857', 'EPSG:4326');
  const coordsB: number[] = transform([coordinates[2], coordinates[3]], 'EPSG:3857', 'EPSG:4326');
  const coords = [coordsA, coordsB];
  const x1 = Number(coords[0][0]);
  const x2 = Number(coords[1][0]);
  const y1 = Number(coords[0][1]);
  const y2 = Number(coords[1][1]);
  const dx = x1 - x2;
  const dy = y2 - y1;
  const x1Direction = x1 > x2 ? -Math.abs(x2 - x1) : Math.abs(x2 - x1);
  const x2Direction = x1 > x2 ? Math.abs(x2 - x1) : -Math.abs(x2 - x1);
  const y1Direction = y1 > y2 ? -Math.abs(y2 - y1) : Math.abs(y2 - y1);
  const y2Direction = y1 > y2 ? Math.abs(y2 - y1) : -Math.abs(y2 - y1);
  const rotation = -Math.atan2(dx, dy);

  const point1 = new Point(
    transform([x1 + x1Direction * 0.1, y1 + y1Direction * 0.1], 'EPSG:4326', 'EPSG:3857'),
  );
  const point2 = new Point(
    transform([x1 + x1Direction * 0.3, y1 + y1Direction * 0.3], 'EPSG:4326', 'EPSG:3857'),
  );
  const point3 = new Point(
    transform([x2 + x2Direction * 0.3, y2 + y2Direction * 0.3], 'EPSG:4326', 'EPSG:3857'),
  );
  const point4 = new Point(
    transform([x2 + x2Direction * 0.1, y2 + y2Direction * 0.1], 'EPSG:4326', 'EPSG:3857'),
  );

  return [
    tripwireArrowStyle(point1, rotation),
    tripwireArrowStyle(point2, rotation),
    tripwireArrowStyle(point3, rotation),
    tripwireArrowStyle(point4, rotation),
  ];
};

/* Geofence Markers */
const geofenceMarkerStyle = (props: { geometry?: Point; fenceType?: GeofenceEntityType }) => {
  const src =
    props.fenceType === GeofenceEntityType.Line
      ? '../../../markerLine.png'
      : '../../../markerPolygon.png';
  return new Style({
    geometry: props.geometry,
    image: new Icon({
      src,
    }),
    zIndex: ZIndexes.MARKER,
  });
};

/* Events */
export const defaultGeofenceEventOverlay = (className: string): LayerParts => {
  const src = new VectorSource();
  return {
    src: src,
    lyr: new VectorLayer({
      className,
      source: src,
      style: geofenceEventStyle(new Map()),
      zIndex: ZIndexes.MARKER,
    }),
  };
};

const GEOFENCE_EVENT_SELECTED_SCALE = 2;
const GEOFENCE_EVENT_DEFAULT_SCALE = 1.2;

export const geofenceEventStyle = (cache: Map<string, Style>): StyleFunction => {
  return (feature: Feature<Geometry> | RenderFeature) => {
    const eventData = feature.get(EVENT_DATA);
    if (!eventData) return;

    const isSelected = !!eventData.selected;
    const eventDescription = eventData.eventDescription;

    // Map event description to internal event type
    const event:
      | EBTTriggerTypeOptionsValue.Entry
      | EBTTriggerTypeOptionsValue.Exit
      | EBTTriggerTypeOptionsValue.Dwell =
      eventDescription === EBTTriggerTypeOptionsValue.LeftToRight
        ? 'LeftToRight'
        : eventDescription === EBTTriggerTypeOptionsValue.RightToLeft
        ? 'RightToLeft'
        : eventDescription ?? EBTTriggerTypeOptionsValue.Exit;

    const source = eventData.isMobileEvent ? 'app' : 'backend';
    // Selection must be part of the key: the previous cache shared one Style
    // (and its Icon) across selected and unselected events of the same type, so
    // setImageScale on the last feature rendered stole the scale from the rest.
    const key = `${eventDescription}_${source}_${isSelected ? 'selected' : 'default'}`;

    const cached = cache.get(key);
    if (cached) return cached;

    const image = iconByEvent[source][event].clone();
    image.setScale(isSelected ? GEOFENCE_EVENT_SELECTED_SCALE : GEOFENCE_EVENT_DEFAULT_SCALE);
    const style = new Style({ image });
    cache.set(key, style);
    return style;
  };
};

/* Interaction */
export const interactionStyle = (
  interaction: Interactions,
  layerSource: VectorSource<Geometry>,
  type: string,
  color: string,
) => {
  const style = [
    new Style({
      image: new Circle({
        radius: 6.5,
        fill: new Fill({
          color: WHITE,
        }),
      }),
      stroke: new Stroke({
        color,
        width: geofenceStyleDefaults.selected.width,
      }),
      zIndex: ZIndexes.LINE,
    }),
    new Style({
      image: new Circle({
        radius: 5,
        fill: new Fill({
          color,
        }),
      }),
      stroke: new Stroke({
        color,
        width: geofenceStyleDefaults.default.width,
      }),
      zIndex: ZIndexes.MARKER,
    }),
  ];

  if (interaction === Interactions.Modify) {
    return new Modify({
      source: layerSource,
      style,
    });
  }

  return new Draw({
    source: layerSource,
    type,
    stopClick: true,
    style,
  });
};
